import React, { useState, useEffect, useMemo } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Divider,
  Collapse,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Chip
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  Assignment as AssignmentIcon,
  Assessment as ReportsIcon,
  BarChart as BarChartIcon,
  Settings as SettingsIcon,
  Tune as TuneIcon,
  Category as CategoryIcon,
  Straighten as StraightenIcon,
  ExpandLess,
  ExpandMore,
  Logout as LogoutIcon,
  AccountCircle as AccountIcon,
  AdminPanelSettings as AdminIcon,
  Security as SecurityIcon,
  EmailOutlined as EmailTemplateIcon,
  Queue as QueueIcon,
  Receipt as ReceiptIcon,
  Money as MoneyIcon,
  Inventory as InventoryIcon,
  LocationOn as LocationIcon,
  Inventory2 as KardexIcon,
  ShoppingCart as SalesIcon,
  Public as OriginIcon,
  LocalShipping as ProviderIcon,
  School as SchoolIcon,
  Loop as CycleIcon,
  Class as GradeIcon,
  MenuBook as CursoIcon,
  LibraryBooks as PensumIcon,
  Assignment as NotaIcon,
  EventNote as PlanificationIcon,
  HowToReg as AttendanceIcon,
  SupervisorAccount as TeacherAssignIcon,
  AccountTree as PlanificationTypeIcon,
  PersonAdd as PersonAddIcon,
  Group as GroupIcon,
  Discount as DiscountIcon,
  Payment as PaymentIcon,
  CardMembership as MembershipIcon,
  CleaningServices as CleaningServicesIcon,
  CalendarViewWeek as CalendarViewWeekIcon,
  HomeWork as HomeWorkIcon,
  Loyalty as LoyaltyIcon,
  RestaurantMenu as RestaurantMenuIcon,
  PointOfSale as PointOfSaleIcon,
  Redeem as RedeemIcon,
  Campaign as CampaignIcon,
  ReceiptLong as ReceiptLongIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from '../../hooks/useTranslation';
import http from '../../http-common';
import { resolveAbsoluteUrl } from '../../utils/assetUrl';
import { hasAttendanceAccess as checkAttendanceAccess } from '../../utils/permissionAliases';

const drawerWidth = 280;

const Sidebar = ({ open, onClose, isMobile }) => {
  const [expandedItems, setExpandedItems] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const { user, logout, hasPermission, hasAnyPermission, hasRole, permissions } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [tenantLogoUrl, setTenantLogoUrl] = useState(null);

  useEffect(() => {
    const loadTenantLogo = async () => {
      try {
        const response = await http.get('/settings');
        setTenantLogoUrl(response.data?.logo_url || null);
      } catch (error) {
        console.error('Error loading tenant logo:', error);
      }
    };
    loadTenantLogo();
  }, []);

  const tenantLogoSrc = useMemo(() => resolveAbsoluteUrl(tenantLogoUrl), [tenantLogoUrl]);
  const tenantDisplayName = user?.tenant?.name?.trim() || 'Techno';

  const isSchool = user?.tenant?.profile === 'school';
  const isMenuProfile = user?.tenant?.profile === 'menu';

  const schoolMenuItems = [
    {
      title: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/dashboard',
    },
    {
      title: t('nav.students'),
      icon: <SchoolIcon />,
      path: '/students',
      permission: 'students.view',
    },
    {
      title: t('nav.cycles'),
      icon: <CycleIcon />,
      path: '/cycles',
      permission: 'cycles.view',
    },
    {
      title: t('nav.grades'),
      icon: <GradeIcon />,
      path: '/grades',
      permission: 'grades.view',
    },
    {
      title: t('nav.cursos'),
      icon: <CursoIcon />,
      path: '/cursos',
      permission: 'cursos.view',
    },
    {
      title: t('nav.pensums'),
      icon: <PensumIcon />,
      path: '/pensums',
      permission: 'pensums.view',
    },
    {
      title: t('nav.attendance'),
      icon: <AttendanceIcon />,
      path: '/attendance',
      attendanceAccess: true,
    },
    {
      title: t('nav.teacherAssignments'),
      icon: <TeacherAssignIcon />,
      path: '/attendance/assignments',
      permission: 'attendances.manage',
    },
    {
      title: t('nav.planification'),
      icon: <PlanificationIcon />,
      path: '/planifications',
      permission: 'planifications.view',
      children: [
        {
          title: t('nav.planifications'),
          path: '/planifications',
          permission: 'planifications.view',
          icon: <PlanificationIcon />,
        },
        {
          title: t('nav.types'),
          path: '/planifications/types',
          permission: 'planification-types.view',
          icon: <PlanificationTypeIcon />,
        },
      ],
    },
    {
      title: t('nav.notas'),
      icon: <NotaIcon />,
      path: '/notas',
      permission: 'notas.view',
    },
    {
      title: t('reports.title'),
      icon: <BarChartIcon />,
      path: '/reports',
      permission: 'reports.view',
    },
  ];

  const defaultMenuItems = [
    {
      title: t('nav.dashboard'),
      icon: <DashboardIcon />,
      path: '/dashboard',
    },
    {
      title: t('clients.fields'),
      icon: <DashboardIcon />,
      path: '/client-fields',
      permission: 'clients.fields'
    },
    {
      title: t ('clients.title'),
      icon: <PeopleIcon />,
      path: '/clients',
      permission: 'clients.view'
    },
    {
      title: t('nav.visits'),
      icon: <AssignmentIcon />,
      path: '/visits',
      permission: 'visits.view'
    },
    {
      title: t('calendar.title'),
      icon: <CalendarIcon />,
      path: '/calendar',
      permission: 'calendar.view'
    },
    {
      title: t('nav.jobSchedule'),
      icon: <CalendarViewWeekIcon />,
      path: '/cleaning-schedule',
      permission: 'cleaning-schedule.view'
    },
    {
      title: t('nav.cleaningDetails'),
      icon: <HomeWorkIcon />,
      path: '/cleaning-details',
      permission: 'cleaning-details.view'
    },
    {
      title: t('nav.employees'),
      icon: <CleaningServicesIcon />,
      path: '/employees',
      permission: 'employees.view'
    },
    {
      title:  t ('services.title'),
      icon: <BusinessIcon />,
      path: '/services',
      permission: 'services.view',
      children: [
        {
          title: t ('services.sertype'),
          path: '/services',
          permission: 'services.view'
        },
        {
          title: t ('serviceHistory.title'),
          path: '/services/history',
          permission: 'service-history.view'
        }
      ]
    },
    
    {
      title:  t ('invoices.title'),
      icon: <ReceiptIcon />,
      path: '/invoices',
      permission: 'invoices.view'
    },
    {
      title: t ('budgets.title'),
      icon: <MoneyIcon />,
      path: '/budgets',
      permission: 'budgets.view'
    },
    {
      title: t('nav.budgetConfig'),
      icon: <TuneIcon />,
      path: '/budget-config/types',
      permission: 'budget-config.view',
      children: [
        {
          title: t('nav.types'),
          path: '/budget-config/types',
          permission: 'budget-types.view',
          icon: <CategoryIcon />
        },
        {
          title: t('nav.units'),
          path: '/budget-config/units',
          permission: 'budget-units.view',
          icon: <StraightenIcon />
        }
      ]
    },
    {
      title: t('nav.loyalty'),
      icon: <LoyaltyIcon />,
      path: '/loyalty',
      permission: 'loyalty.view',
      children: [
        {
          title: t('nav.loyaltyCashier'),
          path: '/loyalty',
          permission: 'loyalty.view',
          icon: <PointOfSaleIcon />,
        },
        {
          title: t('nav.loyaltyMembers'),
          path: '/loyalty/clients',
          permission: 'loyalty.view',
          icon: <PeopleIcon />,
        },
        {
          title: t('nav.loyaltyRewards'),
          path: '/loyalty/rewards',
          permission: 'loyalty.view',
          icon: <RedeemIcon />,
        },
        {
          title: t('nav.loyaltyPromotions'),
          path: '/loyalty/promotions',
          permission: 'loyalty.view',
          icon: <CampaignIcon />,
        },
        {
          title: t('nav.loyaltyTransactions'),
          path: '/loyalty/transactions',
          permission: 'loyalty.view',
          icon: <ReceiptLongIcon />,
        },
        {
          title: t('nav.loyaltySettings'),
          path: '/loyalty/settings',
          permission: 'loyalty.manage',
          icon: <TuneIcon />,
        },
      ],
    },
    {
      title: t('nav.menus'),
      icon: <RestaurantMenuIcon />,
      path: '/menus',
      permission: 'menu.view',
    },
    {
      title: t('nav.products'),
      icon: <InventoryIcon />,
      path: '/products',
      permission: 'products.view',
    },
    {
      title: t('locations.title'),
      icon: <LocationIcon />,
      path: '/locations',
      permission: 'locations.view',
    },
    {
      title: t('inventories.title'),
      icon: <KardexIcon />,
      path: '/inventories',
      permission: 'inventories.view',
    },
    {
      title: t('sales.title'),
      icon: <SalesIcon />,
      path: '/sales',
      permission: 'sales.view',
    },
    {
      title: t('origins.title'),
      icon: <OriginIcon />,
      path: '/origins',
      permission: 'origins.view',
    },
    {
      title: t('providers.title'),
      icon: <ProviderIcon />,
      path: '/providers',
      permission: 'providers.view',
    },
    {
      title: t('reports.title'),
      icon: <BarChartIcon />,
      path: '/reports',
      permission: 'reports.view',
    },
    {
      title: t('nav.students'),
      icon: <SchoolIcon />,
      path: '/students',
      permission: 'students.view'
    },
    {
      title: t('nav.cycles'),
      icon: <CycleIcon />,
      path: '/cycles',
      permission: 'cycles.view'
    },
    {
      title: t('nav.grades'),
      icon: <GradeIcon />,
      path: '/grades',
      permission: 'grades.view'
    },
    {
      title: t('nav.cursos'),
      icon: <CursoIcon />,
      path: '/cursos',
      permission: 'cursos.view'
    },
    {
      title: t('nav.pensums'),
      icon: <PensumIcon />,
      path: '/pensums',
      permission: 'pensums.view'
    },
    {
      title: t('nav.notas'),
      icon: <NotaIcon />,
      path: '/notas',
      permission: 'notas.view'
    },
    {
      title: t('nav.attendance'),
      icon: <AttendanceIcon />,
      path: '/attendance',
      attendanceAccess: true,
    },
    {
      title: t('nav.teacherAssignments'),
      icon: <TeacherAssignIcon />,
      path: '/attendance/assignments',
      permission: 'attendances.manage',
    },
    {
      title: t('nav.planification'),
      icon: <PlanificationIcon />,
      path: '/planifications',
      permission: 'planifications.view',
      children: [
        {
          title: t('nav.planifications'),
          path: '/planifications',
          permission: 'planifications.view',
          icon: <PlanificationIcon />
        },
        {
          title: t('nav.types'),
          path: '/planifications/types',
          permission: 'planification-types.view',
          icon: <PlanificationTypeIcon />
        }
      ]
    },
    {
      title: t('nav.descounts'),
      icon: <DiscountIcon />,
      path: '/descounts',
      permission: 'descuento.list'
    },
    {
      title: t('nav.paymentTypes'),
      icon: <PaymentIcon />,
      path: '/payment-types',
      permission: 'paytypes.list'
    },
    {
      title: t('nav.memberships'),
      icon: <MembershipIcon />,
      path: '/memberships',
      permission: 'clientpayments.list'
    },
    {
      title: t('nav.payments'),
      icon: <PaymentIcon />,
      path: '/payments',
      permission: 'payments.list'
    },

  ];

  // Menu-profile tenants only see the digital menu (plus loyalty when they turned it on).
  const menuProfilePaths = ['/dashboard', '/menus', ...(user?.tenant?.settings?.loyalty?.enabled ? ['/loyalty'] : [])];
  const menuItems = isSchool
    ? schoolMenuItems
    : isMenuProfile ? defaultMenuItems.filter((item) => menuProfilePaths.includes(item.path)) : defaultMenuItems;

  useEffect(() => {
    const path = location.pathname;
    setExpandedItems((prev) => {
      const next = { ...prev };
      let changed = false;

      const expand = (title, paths) => {
        if (paths.includes(path) && !next[title]) {
          next[title] = true;
          changed = true;
        }
      };

      expand(t('nav.attendance'), ['/attendance', '/attendance/assignments']);
      expand(t('services.title'), ['/services', '/services/history']);
      expand(t('nav.budgetConfig'), ['/budget-config/types', '/budget-config/units']);
      expand(t('nav.planification'), ['/planifications', '/planifications/types']);

      return changed ? next : prev;
    });
  }, [location.pathname, t]);

  const adminItems = [
    {
      title: t('nav.userManagement'),
      icon: <PeopleIcon />,
      path: '/admin/users',
      permission: 'users.view'
    },
    {
      title: t('nav.tenantUsers'),
      icon: <GroupIcon />,
      path: '/admin/tenant-users',
      permission: 'users.view'
    },
    {
      title: t('nav.tenantManagement'),
      icon: <BusinessIcon />,
      path: '/admin/tenants',
      permission: 'tenants.view'
    },
    {
      title: t('nav.tenantRoles'),
      icon: <SecurityIcon />,
      path: '/admin/tenant-roles',
      permission: 'tenantroles.view'
    },
    {
      title: t('nav.tenantRegistration'),
      icon: <PersonAddIcon />,
      path: '/register',
      permission: 'tenants.create'
    },
    {
      title: t('nav.rolesPermissions'),
      icon: <AdminIcon />,
      path: '/admin/roles',
      permission: 'roles.view'
    },
    {
      title: t('nav.permissionManagement'),
      icon: <SecurityIcon />,
      path: '/admin/permissions',
      permission: 'permissions.view'
    },
    {
      title: t('nav.emailTemplates'),
      icon: <EmailTemplateIcon />,
      path: '/admin/email-templates',
      permission: 'emails.view'
    },
    {
      title: t('nav.emailQueue'),
      icon: <QueueIcon />,
      path: '/admin/email-queue',
      permission: 'emails.queue.view'
    },
    {
      title: t('nav.systemSettings'),
      icon: <SettingsIcon />,
      path: '/settings',
      permission: 'settings.view'
    }
  ];

  const handleItemClick = (path) => {
    if (path && path !== location.pathname) {
      navigate(path);
    }
    if (isMobile && onClose) {
      onClose();
    }
  };

  const handleExpandClick = (itemTitle) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemTitle]: !prev[itemTitle]
    }));
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const itemHasAccess = (item) => {
    if (hasRole('admin')) {
      return true;
    }
    if (item.attendanceAccess) {
      return checkAttendanceAccess(permissions);
    }
    if (Array.isArray(item.permissions) && item.permissions.length > 0) {
      return hasAnyPermission(item.permissions);
    }
    if (!item.permission) {
      return true;
    }
    return hasPermission(item.permission);
  };

  const renderMenuItem = (item, level = 0) => {
    const children = item.children || [];
    const visibleChildren = children.filter((child) => itemHasAccess(child));
    const hasChildren = visibleChildren.length > 0;
    const allowed = itemHasAccess(item);

    if (children.length > 0 && visibleChildren.length === 0) {
      return null;
    }

    if (!allowed && !hasChildren) {
      return null;
    }

    if (!hasChildren && !item.path) {
      return null;
    }

    const isExpanded = expandedItems[item.title];
    const isChildActive = hasChildren
      ? visibleChildren.some((child) => child.path && location.pathname === child.path)
      : false;
    const isActive = hasChildren
      ? isChildActive || (item.path ? location.pathname === item.path : false)
      : (item.path ? location.pathname === item.path : false);

    return (
      <React.Fragment key={item.path || `${item.title}-${level}`}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => (hasChildren ? handleExpandClick(item.title) : handleItemClick(item.path))}
            sx={{
              pl: 2 + level * 2,
              backgroundColor: isActive ? 'primary.main' : 'transparent',
              color: isActive ? 'white' : 'inherit',
              '&:hover': {
                backgroundColor: isActive ? 'primary.dark' : 'action.hover',
              },
              borderRadius: 1,
              mx: 1,
              mb: 0.5
            }}
          >
            <ListItemIcon sx={{ color: isActive ? 'white' : 'inherit', minWidth: 40 }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText 
              primary={item.title}
              primaryTypographyProps={{
                fontSize: '0.9rem',
                fontWeight: isActive ? 600 : 400
              }}
            />
            {hasChildren && (
              isExpanded ? <ExpandLess /> : <ExpandMore />
            )}
          </ListItemButton>
        </ListItem>
        {hasChildren && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {visibleChildren.map(child => renderMenuItem(child, level + 1))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'persistent'}
      anchor="left"
      open={open}
      onClose={isMobile ? onClose : undefined}
      ModalProps={{
        keepMounted: true, // Better mobile performance
      }}
      sx={{
        width: open ? drawerWidth : 0,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          top: isMobile ? 0 : '64px', // Below AppBar on desktop, full height on mobile
          height: isMobile ? '100%' : 'calc(100% - 64px)',
          position: 'fixed'
        },
      }}
    >
      <Box sx={{ p: 2 }}>
        <Box
          role="link"
          title={t('nav.dashboard')}
          onClick={() => navigate('/dashboard')}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            mb: 2,
            width: '100%',
            cursor: 'pointer',
          }}
        >
          {tenantLogoSrc ? (
            <Box
              component="img"
              src={tenantLogoSrc}
              alt={tenantDisplayName}
              sx={{ maxHeight: 52, maxWidth: '100%', width: 'auto', objectFit: 'contain' }}
            />
          ) : (
            <BusinessIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          )}
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            color="primary.main"
            textAlign="center"
            sx={{ lineHeight: 1.25, px: 0.5, wordBreak: 'break-word' }}
          >
            {tenantDisplayName}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={handleProfileMenuOpen} sx={{ p: 0, mr: 1 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </Avatar>
          </IconButton>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              {user?.name || 'User'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.email || 'user@example.com'}
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              {user?.roles?.map(role => (
                <Chip
                  key={typeof role === 'string' ? role : role.name}
                  label={typeof role === 'string' ? role : role.name}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ mr: 0.5, fontSize: '0.7rem', height: 20 }}
                />
              ))}
            </Box>
          </Box>
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleProfileMenuClose}
        >
          <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/profile'); }}>
            <ListItemIcon>
              <AccountIcon fontSize="small" />
            </ListItemIcon>
            {t('nav.profile')}
          </MenuItem>
          <MenuItem onClick={() => { handleProfileMenuClose(); handleLogout(); }}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            {t('nav.logout')}
          </MenuItem>
        </Menu>
      </Box>

      <Divider />

      <List sx={{ px: 1, py: 2 }}>
        {menuItems.map(item => renderMenuItem(item))}
      </List>

      {(hasRole('superadmin') || adminItems.some((item) => itemHasAccess(item))) && (
        <>
          <Divider sx={{ mx: 2, my: 1 }} />
          <Typography variant="overline" sx={{ px: 3, py: 1, color: 'text.secondary', fontWeight: 'bold' }}>
            {t('nav.administration')}
          </Typography>
          <List sx={{ px: 1 }}>
            {adminItems.map(item => renderMenuItem(item))}
          </List>
        </>
      )}
    </Drawer>
  );
};

export default Sidebar;
